import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { formatCode } from "../share-records/md";

enum SidebarPosition {
  Open,
  Close,
  FullScreen,
}

enum SidebarTab {
  Code,
  Preview,
}

interface ArtifactSidebarProps {
  children: React.ReactNode;
  code: string;
}

let defaultSidebarWidth: string | number = 700;
try {
  defaultSidebarWidth = window.innerWidth > 768 ? 700 : "100%";
} catch {}

const useSidebarWidth = (position: SidebarPosition) => {
  const [width, setWidth] = useState(defaultSidebarWidth);

  useEffect(() => {
    const onResize = () => {
      setWidth(window.innerWidth > 768 ? 700 : "100%");
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return useMemo(() => {
    if (position === SidebarPosition.Open) {
      return width;
    } else if (position === SidebarPosition.FullScreen) {
      return "100%";
    } else if (position === SidebarPosition.Close) {
      return 0;
    } else {
      throw new Error("Invalid sidebar position");
    }
  }, [position, width]);
};

export type ArtifactSidebarRef = {
  open: () => void;
};

export const ArtifactSidebar = forwardRef<ArtifactSidebarRef, ArtifactSidebarProps>(({ children, code }, ref) => {
  const [position, setPosition] = useState(SidebarPosition.Close);
  const [tab, setTab] = useState(SidebarTab.Preview);

  const sidebarWidth = useSidebarWidth(position);

  useImperativeHandle(
    ref,
    () => ({
      open: () => setPosition(SidebarPosition.Open),
    }),
    [position]
  );

  return (
    <div className="absolute left-0 top-0 size-full flex h-full overflow-hidden">
      <div className="flex-1 overflow-auto">
        <div className="min-w-[100vw] md:min-w-full overflow-hidden">{children}</div>
      </div>
      <div
        className="h-full transition-width delay-0 duration-500 shadow-[0_0_50px_0_#0000000a]"
        style={{ width: sidebarWidth }}
      >
        <div
          style={{ minWidth: sidebarWidth, overflow: "hidden", height: "100%" }}
          className="bg-[#fbfbfb] dark:bg-[#121212]"
        >
          <div className="rounded-tl-[25px] bg-[var(--artifacts-code-head-bg)] border border-[var(--artifacts-code-border)] p-[20px] flex items-center justify-between">
            <div className="flex items-center gap-[10px]">
              <div
                className="h-[36px] w-[36px] flex cursor-pointer items-center justify-center border border-[var(--artifacts-code-border)] rounded-[8px] bg-[var(--artifacts-code-bg)]"
                onClick={() => {
                  setPosition(SidebarPosition.Close);
                }}
              >
                <svg
                  display="inline-block"
                  vertical-align="middle"
                  width="10px"
                  height="10px"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 10 11"
                  className="design-iconfont"
                >
                  <path
                    d="M39.0940063,41.3019922 L35,45.396007 L36.2079945,46.603993 L41.5099781,41.3019922 L36.2079945,36 L35,37.2079945 L39.0940063,41.3019922 Z M45,36.1769099 L45,46.4270916 L43.2916335,46.4270916 L43.2916335,36.1769099 L45,36.1769099 Z"
                    transform="translate(-35 -36)"
                    fill="#727272"
                    fillRule="nonzero"
                  />
                </svg>
              </div>
              <div
                className="h-[36px] w-[36px] flex cursor-pointer items-center justify-center border border-[var(--artifacts-code-border)] rounded-[8px] bg-[var(--artifacts-code-bg)]"
                onClick={() =>
                  setPosition((pre) =>
                    pre === SidebarPosition.FullScreen ? SidebarPosition.Open : SidebarPosition.FullScreen
                  )
                }
              >
                {position === SidebarPosition.FullScreen ? (
                  <svg
                    viewBox="0 0 24 24"
                    display="inline-block"
                    vertical-align="middle"
                    width="16px"
                    height="16px"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path fill="#727272" d="M18 7h4v2h-6V3h2zM8 9H2V7h4V3h2zm10 8v4h-2v-6h6v2zM8 15v6H6v-4H2v-2z" />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    display="inline-block"
                    vertical-align="middle"
                    width="16px"
                    height="16px"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path fill="#727272" d="M8 3v2H4v4H2V3zM2 21v-6h2v4h4v2zm20 0h-6v-2h4v-4h2zm0-12h-2V5h-4V3h6z" />
                  </svg>
                )}
              </div>
            </div>
          </div>
          <div className="relative w-full border-l-0 md:border-l border-[var(--artifacts-code-border)] h-[calc(100vh-216px)] md:h-[calc(100vh-238px)]">
            <div
              className="absolute w-full h-full overflow-auto"
              style={{
                opacity: tab === SidebarTab.Code ? 1 : 0,
                zIndex: tab === SidebarTab.Code ? 1 : 0,
                pointerEvents: tab === SidebarTab.Code ? "auto" : "none",
              }}
            >
              <div className="whitespace-pre px-[20px] pb-[40px] text-[12px]">
                <code className="whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: formatCode(code) }} />
              </div>
            </div>
            <div
              className="absolute w-full h-full overflow-hidden"
              style={{
                opacity: tab === SidebarTab.Preview ? 1 : 0,
                zIndex: tab === SidebarTab.Preview ? 1 : 0,
                pointerEvents: tab === SidebarTab.Preview ? "auto" : "none",
              }}
            >
              <iframe className="size-full overflow-auto" srcDoc={code} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
